import React, { useState, useEffect } from 'react';
import { Upload, Trash2, ExternalLink, Globe } from 'lucide-react';
import { getPartners, uploadPartner, deletePartner } from '../utils/api';

const AdminPartners = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  const [uploadData, setUploadData] = useState({
    logo: null,
    websiteUrl: ''
  });

  useEffect(() => {
    fetchPartners();
  }, []);

  const fetchPartners = async () => {
    try {
      setLoading(true);
      const data = await getPartners();
      setPartners(data);
    } catch (err) {
      setError('Failed to load partners');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file');
        return;
      }
      setUploadData({ ...uploadData, logo: file });
      setError(null);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    
    if (!uploadData.logo) {
      setError('Please select a logo image');
      return;
    }
    
    if (!uploadData.websiteUrl) {
      setError('Please enter a website URL');
      return;
    }

    setUploading(true);
    setError(null);
    setSuccess(null);

    try {
      await uploadPartner(uploadData.logo, uploadData.websiteUrl);
      setSuccess('Partner logo uploaded successfully!');
      setUploadData({ logo: null, websiteUrl: '' });
      // Reset file input
      document.getElementById('partner-logo-input').value = '';
      await fetchPartners();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to upload partner logo');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (partnerId, filename) => {
    if (!window.confirm('Are you sure you want to delete this partner?')) {
      return;
    }

    try {
      await deletePartner(partnerId);
      setSuccess('Partner deleted successfully');
      await fetchPartners();
    } catch (err) {
      setError('Failed to delete partner');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Partner Management</h1>
        <p className="mt-2 text-gray-600">Upload and manage partner logos</p>
      </div>

      {/* Upload Form */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold mb-4">Upload Partner Logo</h2>
        
        <form onSubmit={handleUpload} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Partner Website URL
            </label>
            <div className="flex items-center space-x-2">
              <Globe className="w-5 h-5 text-gray-400" />
              <input
                type="url"
                value={uploadData.websiteUrl}
                onChange={(e) => setUploadData({ ...uploadData, websiteUrl: e.target.value })}
                placeholder="https://www.partner-website.com"
                required
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Logo Image
            </label>
            <input
              id="partner-logo-input"
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
            />
            {uploadData.logo && (
              <p className="mt-2 text-sm text-gray-600">
                Selected: {uploadData.logo.name}
              </p>
            )}
          </div>

          {error && (
            <div className="p-4 bg-red-50 text-red-800 rounded-lg">
              {error}
            </div>
          )}

          {success && (
            <div className="p-4 bg-green-50 text-green-800 rounded-lg">
              {success}
            </div>
          )}

          <button
            type="submit"
            disabled={uploading || !uploadData.logo || !uploadData.websiteUrl}
            className="flex items-center space-x-2 bg-cyan-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-cyan-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Upload className="w-5 h-5" />
            <span>{uploading ? 'Uploading...' : 'Upload Partner Logo'}</span>
          </button>
        </form>
      </div>

      {/* Partners List */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold mb-4">Current Partners ({partners.length})</h2>
        
        {loading ? (
          <p className="text-gray-600">Loading partners...</p>
        ) : partners.length === 0 ? (
          <p className="text-gray-600">No partners yet. Upload your first partner logo above.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {partners.map((partner) => (
              <div key={partner.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-lg transition-shadow">
                <div className="aspect-video bg-gray-50 rounded-lg mb-3 flex items-center justify-center overflow-hidden">
                  <img
                    src={partner.logo_url}
                    alt="Partner logo"
                    className="max-w-full max-h-full object-contain p-2"
                  />
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-start space-x-2">
                    <ExternalLink className="w-4 h-4 text-gray-400 flex-shrink-0 mt-1" />
                    <a
                      href={partner.website_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-cyan-600 hover:text-cyan-800 break-all"
                    >
                      {partner.website_url}
                    </a>
                  </div>
                  
                  <div className="text-xs text-gray-500">
                    Uploaded: {new Date(partner.uploaded_at).toLocaleDateString()}
                  </div>
                  
                  <button
                    onClick={() => handleDelete(partner.id, partner.filename)}
                    className="w-full flex items-center justify-center space-x-2 bg-red-50 text-red-600 px-4 py-2 rounded-lg hover:bg-red-100 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                    <span>Delete</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPartners;
